import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ContratosDetalleModal } from "@/components/ContratosDetalleModal";
import { MultiSelect } from "@/components/MultiSelect";
import {
  type ContratoDashboardRow,
  getContratosCatalogos,
  getContratosDashboard,
} from "@/lib/api";

// Paleta para emisores en la gráfica apilada.
const PALETTE = [
  "#2563eb", "#dc2626", "#16a34a", "#ca8a04", "#9333ea",
  "#0891b2", "#ea580c", "#db2777", "#65a30d", "#475569",
  "#7c3aed", "#0d9488", "#b91c1c", "#c2410c", "#0369a1",
];

type SortKey = "anio" | "emisor" | "adquiriente" | "total";

export function ContratosDashboardPage() {
  const [emisores, setEmisores] = useState<string[]>([]);
  const [adquirientes, setAdquirientes] = useState<string[]>([]);
  const [estatus, setEstatus] = useState<string[]>([]);
  const [desde, setDesde] = useState<string>("");
  const [hasta, setHasta] = useState<string>("");
  const [sortKey, setSortKey] = useState<SortKey>("anio");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [detalleOpen, setDetalleOpen] = useState<{
    anio: number;
    emisor: string | null;
    adquiriente: string | null;
  } | null>(null);

  const PAGE_SIZE = 100;

  const catalogos = useQuery({
    queryKey: ["contratos-catalogos"],
    queryFn: getContratosCatalogos,
  });

  const dashboard = useQuery({
    queryKey: ["contratos-dashboard", emisores, adquirientes, estatus, desde, hasta],
    queryFn: () =>
      getContratosDashboard({
        emisores: emisores.length ? emisores : undefined,
        adquirientes: adquirientes.length ? adquirientes : undefined,
        estatus: estatus.length ? estatus : undefined,
        desde: desde || undefined,
        hasta: hasta || undefined,
      }),
  });

  const rows = dashboard.data?.items ?? [];

  // Datos para la gráfica: x = año, una serie por emisor (apiladas).
  const { chartData, chartEmisores, chartColors } = useMemo(() => {
    const byYear = new Map<number, Record<string, number | string>>();
    const emisoresSet = new Set<string>();
    for (const r of rows) {
      const emKey = r.emisor ?? "(sin emisor)";
      emisoresSet.add(emKey);
      const point = byYear.get(r.anio) ?? { anio: r.anio };
      point[emKey] = ((point[emKey] as number) ?? 0) + r.total;
      byYear.set(r.anio, point);
    }
    const data = Array.from(byYear.values()).sort(
      (a, b) => (a.anio as number) - (b.anio as number),
    );
    const emisoresArr = Array.from(emisoresSet).sort();
    const colors: Record<string, string> = {};
    emisoresArr.forEach((e, i) => {
      colors[e] = PALETTE[i % PALETTE.length];
    });
    return { chartData: data, chartEmisores: emisoresArr, chartColors: colors };
  }, [rows]);

  const sortedRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedRows, page],
  );

  const totalContratos = rows.reduce((acc, r) => acc + r.total, 0);

  const handleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("asc");
    }
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <MultiSelect
          label="Emisor"
          options={catalogos.data?.emisores ?? []}
          selected={emisores}
          onChange={(s) => {
            setEmisores(s);
            setPage(0);
          }}
        />
        <MultiSelect
          label="Adquiriente"
          options={catalogos.data?.adquirientes ?? []}
          selected={adquirientes}
          onChange={(s) => {
            setAdquirientes(s);
            setPage(0);
          }}
        />
        <MultiSelect
          label="Estatus"
          options={catalogos.data?.estatus ?? []}
          selected={estatus}
          onChange={(s) => {
            setEstatus(s);
            setPage(0);
          }}
        />
        <Field label="Fecha firma desde">
          <input
            type="date"
            className="border rounded px-2 py-1.5 w-full"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </Field>
        <Field label="Fecha firma hasta">
          <input
            type="date"
            className="border rounded px-2 py-1.5 w-full"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </Field>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2 text-sm">
          <h3 className="font-medium">Contratos por año (apilado por emisor)</h3>
          <span className="text-slate-500">
            {totalContratos.toLocaleString("es-MX")} contratos · {chartEmisores.length} emisores
          </span>
        </div>
        {dashboard.isLoading && (
          <div className="py-16 text-center text-slate-500">Cargando…</div>
        )}
        {chartData.length > 0 && (
          <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="anio" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={50} />
                <Tooltip />
                {chartEmisores.length <= 10 && (
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                )}
                {chartEmisores.map((em) => (
                  <Bar
                    key={em}
                    dataKey={em}
                    stackId="emisor"
                    fill={chartColors[em]}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {chartEmisores.length > 10 && (
              <div className="text-xs text-slate-500 mt-2 italic">
                Leyenda oculta — {chartEmisores.length} emisores. Filtra para verla.
              </div>
            )}
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <Th col="anio" label="Año" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <Th col="emisor" label="Emisor" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <Th col="adquiriente" label="Adquiriente" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <Th col="total" label="Contratos" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r: ContratoDashboardRow, i) => (
                <tr
                  key={i}
                  className="border-t hover:bg-blue-50 cursor-pointer"
                  onClick={() =>
                    setDetalleOpen({
                      anio: r.anio,
                      emisor: r.emisor,
                      adquiriente: r.adquiriente,
                    })
                  }
                  title="Ver detalle de los contratos"
                >
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.anio}</td>
                  <td className="px-3 py-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                      style={{ backgroundColor: chartColors[r.emisor ?? "(sin emisor)"] }}
                    />
                    {r.emisor ?? "—"}
                  </td>
                  <td className="px-3 py-1.5">{r.adquiriente ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                    {r.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm border-t px-3 py-2">
              <div className="text-slate-500">
                {sortedRows.length.toLocaleString("es-MX")} filas · página {page + 1} de {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="border rounded px-3 py-1 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  className="border rounded px-3 py-1 disabled:opacity-50"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page + 1 >= totalPages}
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {detalleOpen && (
        <ContratosDetalleModal
          open
          anio={detalleOpen.anio}
          emisor={detalleOpen.emisor}
          adquiriente={detalleOpen.adquiriente}
          estatus={estatus.length ? estatus : undefined}
          onClose={() => setDetalleOpen(null)}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Th({
  col,
  label,
  sortKey,
  sortDir,
  onSort,
  align,
}: {
  col: SortKey;
  label: string;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onSort: (k: SortKey) => void;
  align?: "right";
}) {
  const active = sortKey === col;
  return (
    <th
      className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 ${
        align === "right" ? "text-right" : ""
      }`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {!active && <ArrowUpDown className="w-3 h-3 opacity-40" />}
        {active && sortDir === "asc" && <ArrowUp className="w-3 h-3" />}
        {active && sortDir === "desc" && <ArrowDown className="w-3 h-3" />}
      </span>
    </th>
  );
}

